/**
 * Workflow Field Helper
 *
 * Provides field data from Object Manager for use in Workflow Rules.
 * This ensures workflows use the same field definitions as the rest of the system.
 */

import { allObjectFields } from '../services/mockData/objectManagerFields';
import { getPicklistValuesAsStrings } from '../services/queries/fieldQueries';

// Object API name to Object ID mapping
const objectApiToId: Record<string, string> = {
  'Lead': 'obj_lead',
  'Client': 'obj_client',
  'Contact': 'obj_contact',
  'Opportunity': 'obj_opportunity',
  'Case': 'obj_case',
  'Task': 'obj_task',
  'Event': 'obj_event',
  'Report': 'obj_report',
  'User': 'obj_user'
};

/**
 * Get all fields for an object from Object Manager
 */
export function getObjectFieldsFromManager(objectApiName: string): {
  apiName: string;
  label: string;
  type: string;
  values?: string[];
}[] {
  const objectId = objectApiToId[objectApiName];
  if (!objectId) {
    console.warn(`Unknown object: ${objectApiName}`);
    return [];
  }

  const fields = allObjectFields[objectId];
  if (!fields) {
    console.warn(`No fields found for object: ${objectApiName} (${objectId})`);
    return [];
  }

  // Map to workflow-friendly format
  return fields.map(field => ({
    apiName: field.fieldName,
    label: field.fieldLabel,
    type: field.fieldType,
    values: getPicklistValuesAsStrings(field.picklistValues)
  }));
}

/**
 * Get picklist values for a specific field from Object Manager
 */
export function getPicklistValuesFromManager(objectApiName: string, fieldApiName: string): string[] {
  const objectId = objectApiToId[objectApiName];
  if (!objectId) return [];

  const fields = allObjectFields[objectId];
  if (!fields) return [];

  const field = fields.find(f => f.fieldName === fieldApiName);
  return getPicklistValuesAsStrings(field?.picklistValues);
}
